import { Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonComponent, CardComponent, IconComponent } from '@webapp/ui';
import { IIssuedPolicyResponse } from '@webapp/payments';
import { firstValueFrom } from 'rxjs';
import { ShowForChannelDirective } from '@webapp/core/directives/showForChannel';
import { IssuePolicyStore } from '../../store/issue-policy-store.service';
import { PoliciesStoreService } from '@webapp/policies/store/policies-store.service';
import { RiskRegistry } from '@webapp/risk-core';

@Component({
  selector: 'app-issue-policy-success-page',
  imports: [
    CardComponent,
    ButtonComponent,
    RouterLink,
    IconComponent,
    ShowForChannelDirective,
  ],
  templateUrl: './issue-policy-success-page.component.html',
  styleUrl: './issue-policy-success-page.component.scss',
})
export class IssuePolicySuccessPageComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private issuePolicyStore = inject(IssuePolicyStore);
  private policiesStore = inject(PoliciesStoreService);
  private riskRegistry = inject(RiskRegistry);
  readonly isNewClient = this.issuePolicyStore.isNewClient;
  downloadingPolicyId = signal<string | null>(null);
  policyResponse!: IIssuedPolicyResponse;
  quoteId?: string;

  ngOnInit(): void {
    // Get quote ID from route
    this.quoteId = this.route.snapshot.paramMap.get('id') || undefined;

    // Get policy response from localStorage
    const storedPolicy = localStorage.getItem('activar_policy');
    if (storedPolicy) {
      try {
        this.policyResponse = JSON.parse(storedPolicy);
      } catch (error) {
        console.error('Error parsing stored policy:', error);
        this.goToNewPolicy(); // Redirect if data is corrupted
      }
    } else {
      console.warn('No policy response found in localStorage');
      this.goToNewPolicy(); // Redirect if no policy data
    }
  }

  viewDetail(policyId: string) {
    this.router.navigate(['polizas', policyId]);
  }

  isVehicle() {
    // First try to get from the store
    const quote = this.issuePolicyStore.quote();
    if (quote?.risk?.tag) {
      return this.isVehicleTag(quote.risk.tag);
    }

    // Fallback to localStorage
    try {
      const riskData = localStorage.getItem('activar_risk');
      if (riskData) {
        const risk = JSON.parse(riskData);
        if (risk?.tag) {
          return this.isVehicleTag(risk.tag);
        }
      }
    } catch (error) {
      console.error('Error parsing risk data from localStorage:', error);
    }

    // Default to false if no data available
    return false;
  }

  private isVehicleTag(tag: string): boolean {
    return this.riskRegistry.find(tag)?.capabilities.vehicle ?? false;
  }

  showName(type: string) {
    if (this.isVehicle()) {
      return type;
    } else {
      return '';
    }
  }

  async downloadCertificate(policyId: string) {
    if (!policyId) {
      console.error('No policy ID available for certificate download');
      return;
    }

    // Get internalNumber from the quote instead of the policy
    const quote = this.issuePolicyStore.quote();
    let internalNumber = 'unknown';

    if (quote?.internalNumber) {
      internalNumber = quote.internalNumber.toString();
    } else {
      console.warn('No internalNumber found in quote, using default');
    }

    this.downloadingPolicyId.set(policyId);
    try {
      const blob = await firstValueFrom(this.policiesStore.service.download(policyId));

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `poliza_No_${internalNumber}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading certificate:', error);
      // You could show a toast error here if needed
    } finally {
      this.downloadingPolicyId.set(null);
    }
  }

  isDownloading(policyId: string): boolean {
    return this.downloadingPolicyId() === policyId;
  }

  goToNewPolicy() {
    this.router.navigate(['polizas']);
  }

  goHome() {
    this.router.navigate(['/dashboard']);
  }
}